// src/oura/merchant/Access.tsx
//
// The merchant's Set-up screen: the 4 accesses we ask for, where each one
// stands, and a way to send the steps to whoever looks after them. Each row
// opens the AccessDrawer, which is where access is given and removed.

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { BarChart3Icon, ChevronDownIcon, GlobeIcon, InfoIcon, Loader2Icon, MapPinIcon, SearchIcon, SendIcon, ShieldCheckIcon, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { safeConnectRedirect } from "../lib/safeRedirect";
import { INLINE_PILL, FOCUS_RING, FoldCard, FoldScope, IconSquare, ListRow, PageHead, PillButton, Seg, Surface, TYPE, type Tone, rowClick } from "@oura/ui";
import { Tooltip } from "@component/tooltip";
import { useViewAs } from "@hook/use-view-as";
import { useWritesPaused } from "@context/maintenance";
import {
    claimMyAccess,
    connectMyAccess,
    getMyOnboarding,
    shareMySteps,
    type AccessItem,
    type AccessKey,
    type MerchantConnectResult,
    type MerchantOnboarding,
    type SiteBuilder,
} from "@service/aeo/onboarding";
import { dayMonth, merchantProgress } from "@oura/onboarding/derive";
import { ACCESS_WHY, ADD_USER_STEPS, GBP_STEPS, GOOGLE_STEPS, GOOGLE_WAITING, MERCHANT_ACCESS_NAME, SITE_BUILDER_ORDER, SITE_GUIDES } from "@oura/onboarding/guides";
import { AccessAddress, GuideSteps } from "@oura/onboarding/StepText";
import { isEmail } from "@oura/onboarding/drafts";
import { AccessDrawer, AccessField, AccessStatusLine, AccessStatusTag } from "./AccessDrawer";
import { CLIENT_HINT } from "../nav";
import { clientArea, CLIENT_PAGE_SENTENCE, MERCHANT_V2 } from "../merchantV2";
import { ONE_SETUP_UI } from "../onboarding/oneSetup";
import { SetupFlow } from "./SetupFlow";

/** "Setup" in the redesign, "Set-up" today (#4062 MR-FE-2). */
const SETUP_TITLE = MERCHANT_V2 ? "Setup" : "Set-up";

/** The order we ask for them in: the one that matters most first. */
const ORDER: AccessKey[] = ["gbp", "website", "gsc", "ga4"];

const ROW_ICON: Record<AccessKey, { icon: LucideIcon; tone: Tone }> = {
    gbp: { icon: MapPinIcon, tone: "blue" },
    website: { icon: GlobeIcon, tone: "teal" },
    gsc: { icon: SearchIcon, tone: "blue" },
    ga4: { icon: BarChart3Icon, tone: "orange" },
};

/** Taller pills on a phone, where they are tapped. */
const TAP = "max-sm:h-9";

/** The first item after `from`, in list order and wrapping, that nobody has started. */
function nextOpen(items: AccessItem[], from: AccessKey): AccessKey | null {
    const at = items.findIndex((a) => a.key === from);
    for (let step = 1; step < items.length; step++) {
        const candidate = items[(at + step) % items.length];
        if (candidate.status === "not_started") return candidate.key;
    }
    return null;
}

function verdict(done: number, total: number, waiting: number, mine: number): string {
    if (done === total) return `${done} of ${total} done. Nothing left to do.`;
    // Nothing is left for the merchant: say so, before the count makes it look unfinished.
    if (mine === 0) return `Your part is done. ${waiting} waiting for us.`;
    if (waiting > 0) return `${done} of ${total} done. ${waiting} waiting for us.`;
    if (done === 0) return `${done} of ${total} done. About 10 minutes in all.`;
    return `${done} of ${total} done.`;
}

/**
 * `embedded`: drawn as the Setup tab inside To do (the redesign's five-item
 * rail), so no page head of its own; the sentence and its promise sit at the
 * top of the tab instead. The full page still answers at /oura/access, from
 * the account menu.
 */
export function Access({ embedded = false }: { embedded?: boolean } = {}) {
    // One setup: the questions and the access as one guided page.
    if (ONE_SETUP_UI) return <SetupFlow embedded={embedded} />;
    return <AccessList embedded={embedded} />;
}

function AccessList({ embedded }: { embedded: boolean }) {
    /** Undefined while loading; null when this business has no set-up. */
    const [data, setData] = useState<MerchantOnboarding | null | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [openKey, setOpenKey] = useState<AccessKey | null>(null);

    useEffect(() => {
        let cancelled = false;
        getMyOnboarding()
            .then((m) => {
                if (cancelled) return;
                setData(m);
                setFailed(false);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [attempt]);

    const retry = () => {
        setFailed(false);
        setData(undefined);
        setAttempt((n) => n + 1);
    };

    if (failed) {
        return (
            <>
                {!embedded && <PageHead title={SETUP_TITLE} />}
                <Surface>
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't load your set-up</h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>Nothing is lost. Try again.</p>
                    <PillButton className={cn(TAP, "mt-4 self-start")} onClick={retry}>
                        Try again
                    </PillButton>
                </Surface>
            </>
        );
    }

    if (data === undefined) {
        return (
            <div role="status" aria-label="Loading your set-up" className="flex justify-center py-24">
                <Loader2Icon aria-hidden className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const rows = data ? ORDER.map((k) => data.access.find((a) => a.key === k)).filter((a): a is AccessItem => Boolean(a)) : [];

    if (!data || rows.length === 0) {
        return (
            <>
                {!embedded && <PageHead title={SETUP_TITLE} />}
                <Surface>
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Nothing to set up yet</h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>Your account manager will start this with you.</p>
                </Surface>
            </>
        );
    }

    const { done, total, waiting, mine } = merchantProgress(data);
    const first = rows.find((a) => a.status === "not_started") ?? null;
    const openIndex = openKey ? rows.findIndex((a) => a.key === openKey) : -1;
    const openItem = openIndex >= 0 ? rows[openIndex] : null;

    return (
        <FoldScope value="merchant-access">
            {/* "Setup", one fixed sentence, and no verdict line: how far the
                set-up has got is on the steps themselves, each of which says
                its own status (#4062 MR-FE-3, MR-FE-9). The head button goes
                with it -- a step is started from its own row. */}
            {embedded ? (
                <p className={cn(TYPE.tc, "m-0 mb-4 text-gray-500")}>
                    <SentenceWithPromise />
                </p>
            ) : (
            <PageHead
                title={SETUP_TITLE}
                sentence={MERCHANT_V2 ? <SentenceWithPromise /> : CLIENT_HINT.access}
                status={MERCHANT_V2 ? undefined : verdict(done, total, waiting, mine)}
                actions={MERCHANT_V2 ? undefined : (
                    first && (
                        <PillButton variant="primary" className={TAP} onClick={() => setOpenKey(first.key)}>
                            {done > 0 ? "Continue set-up" : "Start set-up"}
                        </PillButton>
                    )
                )}
            />
            )}

            <div className="space-y-4">
                {/* The redesign keeps this promise behind the info icon by the
                    page sentence: it is reassurance, not something to act on. */}
                {!MERCHANT_V2 && (
                    <div className="flex gap-3 rounded-2xl bg-emerald-50 p-4 text-emerald-800">
                        <ShieldCheckIcon aria-hidden size={20} className="mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <p className={cn(TYPE.tb, "m-0 font-semibold")}>We never ask for your password</p>
                            <p className={cn(TYPE.tc, "m-0 mt-0.5")}>You add us. You can remove us any time.</p>
                        </div>
                    </div>
                )}

                {MERCHANT_V2 ? (
                    // Flush, so a done row's green runs to the card's edges. Inside
                    // To do's Setup tab the tab is already the card: no second
                    // border, and a done row's green is its own rounded band.
                    embedded ? (
                        <div>
                            {rows.map((item) => (
                                <AccessRow key={item.key} item={item} onboarding={data} onChanged={setData} onManage={() => setOpenKey(item.key)} embedded />
                            ))}
                        </div>
                    ) : (
                        <Surface flush className="overflow-hidden">
                            {rows.map((item) => (
                                <AccessRow key={item.key} item={item} onboarding={data} onChanged={setData} onManage={() => setOpenKey(item.key)} />
                            ))}
                        </Surface>
                    )
                ) : (
                    <Surface flush className="px-5 py-2">
                        {rows.map((item) => {
                            const held = item.status === "confirmed" || item.status === "connected";
                            return (
                                <ListRow
                                    key={item.key}
                                    data-access-row={item.key}
                                    // Done is a green row, not a tick beside one:
                                    // the thing an owner scans for is "which of
                                    // these am I finished with" (#4062 MR-FE-9).
                                    className={MERCHANT_V2 && held ? "rounded-xl bg-emerald-50/70" : undefined}
                                    icon={<IconSquare icon={ROW_ICON[item.key].icon} tone={MERCHANT_V2 ? (held ? "green" : "gray") : ROW_ICON[item.key].tone} />}
                                    title={MERCHANT_ACCESS_NAME[item.key]}
                                    sub={
                                        MERCHANT_V2 ? (
                                            <>
                                                {ACCESS_WHY[item.key]}
                                                <span className="mt-0.5 block">
                                                    <AccessStatusLine item={item} />
                                                </span>
                                            </>
                                        ) : (
                                            ACCESS_WHY[item.key]
                                        )
                                    }
                                    // The right of a row is for something that
                                    // does something. A status pill there read as
                                    // pressable and was not; the status is under
                                    // the name now, and the button says the verb.
                                    right={
                                        MERCHANT_V2 ? (
                                            <span className={cn(INLINE_PILL.base, held ? INLINE_PILL.secondary : INLINE_PILL.primary, INLINE_PILL.row)}>
                                                {held ? "Manage" : item.status === "claimed" ? "Undo" : "Start"}
                                            </span>
                                        ) : (
                                            <AccessStatusTag item={item} />
                                        )
                                    }
                                    onClick={() => setOpenKey(item.key)}
                                />
                            );
                        })}
                    </Surface>
                )}

                {/* Only while something is left that another person could do.
                    The redesign asks first, so the form is not on every visit. */}
                {mine > 0 && (MERCHANT_V2 ? <ShareStepsAsk onboarding={data} onChanged={setData} embedded={embedded} /> : <ShareSteps onboarding={data} onChanged={setData} />)}
            </div>

            {openItem && (
                <AccessDrawer
                    key={openItem.key}
                    item={openItem}
                    onboarding={data}
                    nextKey={nextOpen(rows, openItem.key)}
                    onClose={() => {
                        const row = openItem.key;
                        setOpenKey(null);
                        // After the drawer's own hand-back, which can find nothing to return to
                        // when the drawer was reached through Next.
                        window.setTimeout(() => {
                            const active = document.activeElement;
                            if (!active || active === document.body) document.querySelector<HTMLElement>(`[data-access-row="${row}"]`)?.focus();
                        }, 60);
                    }}
                    onChanged={setData}
                    onNext={() => setOpenKey(nextOpen(rows, openItem.key))}
                />
            )}
        </FoldScope>
    );
}

const NAME_ID = "share-steps-name";
const EMAIL_ID = "share-steps-email";

/**
 * Send the steps to whoever looks after the website or the Google accounts.
 * Embedded in To do's Setup tab, the tab is already the card: no second card,
 * just one more row under the steps, its icon and text in their columns.
 */
function ShareSteps({
    onboarding,
    onChanged,
    embedded = false,
}: {
    onboarding: MerchantOnboarding;
    onChanged: (updated: MerchantOnboarding) => void;
    embedded?: boolean;
}) {
    const contact = onboarding.accessContact;
    // The form's access contact, only when it gave an address to send to.
    const [name, setName] = useState(contact?.email ? contact.name : "");
    const [email, setEmail] = useState(contact?.email ?? "");
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [failure, setFailure] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // Staff viewing as the merchant read; they do not send the steps on (#4112).
    const { active: readOnly } = useViewAs();
    const writesPaused = useWritesPaused();
    const sent = onboarding.sharedWith;

    const send = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (writesPaused) return;
        const found: { name?: string; email?: string } = {};
        if (!name.trim()) found.name = "Add their name.";
        if (!isEmail(email)) found.email = email.trim() ? "Check the email address." : "Add their email.";
        setErrors(found);
        if (found.name || found.email) {
            document.getElementById(found.name ? NAME_ID : EMAIL_ID)?.focus();
            return;
        }
        setBusy(true);
        setFailure(null);
        try {
            const person = { name: name.trim(), email: email.trim() };
            // The line under the form says it was sent, and stays. No toast on top of it.
            onChanged(await shareMySteps(person));
        } catch {
            setFailure("The steps were not sent. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    const body = (
        <>
            <p className={cn(embedded ? TYPE.tc : TYPE.tb, "m-0 text-gray-500")}>Send them the steps. They do not need a login.</p>
            <form noValidate onSubmit={(e) => void send(e)} className="mt-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <AccessField
                        id={NAME_ID}
                        label="Name"
                        autoComplete="off"
                        value={name}
                        error={errors.name}
                        onChange={(v) => {
                            setName(v);
                            if (errors.name) setErrors((was) => ({ ...was, name: undefined }));
                        }}
                    />
                    <AccessField
                        id={EMAIL_ID}
                        label="Email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        error={errors.email}
                        onChange={(v) => {
                            setEmail(v);
                            if (errors.email) setErrors((was) => ({ ...was, email: undefined }));
                        }}
                    />
                </div>
                <PillButton type="submit" variant="primary" busy={busy} writes disabled={readOnly} className={cn(TAP, "mt-4")}>
                    Send the steps
                </PillButton>
                {failure && (
                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-600")}>
                        {failure}
                    </p>
                )}
            </form>
            {sent.length > 0 && (
                <ul className={cn(TYPE.tc, "list-plain m-0 mt-4 space-y-1 p-0 text-gray-500")}>
                    {sent.map((p) => {
                        const when = dayMonth(p.at);
                        return <li key={`${p.email}-${p.at}`}>{when ? `Sent to ${p.name}, ${when}` : `Sent to ${p.name}`}</li>;
                    })}
                </ul>
            )}
        </>
    );

    if (embedded) {
        return (
            <section aria-labelledby="share-steps-title" className="border-t border-gray-100 px-3 pt-4">
                <div className="flex items-start gap-3">
                    <IconSquare icon={SendIcon} tone="gray" className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h3 id="share-steps-title" className={cn(TYPE.tb, "m-0 text-gray-900")}>
                            Someone else looks after this?
                        </h3>
                        {body}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <FoldCard
            id="share"
            title="Someone else looks after this?"
            icon={{ icon: SendIcon, area: clientArea("people") }}
            foldedFact={sent.length > 0 ? `Sent to ${sent.length}` : undefined}
        >
            {body}
        </FoldCard>
    );
}

/**
 * The page sentence, with the password promise behind an info icon beside it
 * (redesign). It used to be a green banner over the list, which took the
 * first screen of a page whose job is the four rows under it.
 */
function SentenceWithPromise() {
    return (
        <span className="inline-flex items-center gap-1.5">
            {CLIENT_PAGE_SENTENCE.access}
            <Tooltip side="right" asChild content="We never ask for your password. You add us, and you can remove us any time.">
                <button type="button" aria-label="We never ask for your password" className={cn("rounded-full text-gray-400 hover:text-gray-600", FOCUS_RING)}>
                    <InfoIcon aria-hidden size={14} />
                </button>
            </Tooltip>
        </span>
    );
}

const NOT_SAVED = "That did not go through. Try again in a moment.";

const WAITING_NOTE: Record<AccessKey, string> = {
    gbp: "We look for your invite and confirm it here.",
    website: "We look for your invite and confirm it here.",
    gsc: GOOGLE_WAITING,
    ga4: GOOGLE_WAITING,
};

/**
 * One access on the redesigned Setup list. The row carries its own action,
 * so a step is done from the list rather than from a drawer that held little
 * more than the button:
 *
 * - Search Console and Analytics: "Connect Google", straight to Google's screen.
 * - Business Profile and the website: "Mark as done", once the owner has added us.
 * - Waiting for us: "Undo". Connected or confirmed: "Manage", which opens the
 *   drawer, the one place to disconnect or read how to remove us.
 *
 * The steps are behind "How to connect?" on the row, open on request. The
 * status sits beside the button, and a row that is done turns green edge to
 * edge. Waiting for us, the reason line says what happens next instead.
 */
const NO_GOOGLE_SCREEN = "We could not open Google's connection screen. Try again in a moment.";

function AccessRow({
    item,
    onboarding,
    onChanged,
    onManage,
    embedded = false,
}: {
    /** In To do's Setup tab: no card around the rows, so a done row is a rounded green band. */
    embedded?: boolean;
    item: AccessItem;
    onboarding: MerchantOnboarding;
    onChanged: (updated: MerchantOnboarding) => void;
    onManage: () => void;
}) {
    const key = item.key;
    const held = item.status === "confirmed" || item.status === "connected";
    const byGoogle = key === "gsc" || key === "ga4";
    const [open, setOpen] = useState(false);
    const [builder, setBuilder] = useState<SiteBuilder>(onboarding.siteBuilder ?? "other");
    const [busy, setBusy] = useState<"primary" | "undo" | null>(null);
    const [failure, setFailure] = useState<string | null>(null);
    const { active: readOnly } = useViewAs();
    const stepsId = useId();
    const guide = SITE_GUIDES[builder];

    const run = async (which: "primary" | "undo", action: () => Promise<MerchantOnboarding>) => {
        setBusy(which);
        setFailure(null);
        try {
            onChanged(await action());
            setOpen(false);
        } catch {
            setFailure(NOT_SAVED);
        } finally {
            setBusy(null);
        }
    };

    const connect = async () => {
        setBusy("primary");
        setFailure(null);
        let result: MerchantConnectResult;
        try {
            result = await connectMyAccess(key, {});
        } catch {
            result = { ok: false, message: NOT_SAVED };
        }
        if (!result.ok) {
            setFailure(result.message);
            setBusy(null);
            return;
        }
        if (result.kind === "redirect") {
            // Google's own screen takes over; the button stays busy until the
            // page leaves. Only an address we expect is followed.
            const to = safeConnectRedirect(result.url);
            if (!to) {
                setFailure(NO_GOOGLE_SCREEN);
                setBusy(null);
                return;
            }
            try {
                window.location.assign(to);
            } catch {
                setFailure(NO_GOOGLE_SCREEN);
                setBusy(null);
            }
            return;
        }
        onChanged(result.onboarding);
        setBusy(null);
    };

    let action: ReactNode = null;
    if (item.status === "not_started") {
        action = (
            <PillButton
                variant="primary"
                className={TAP}
                busy={busy === "primary"}
                writes
                disabled={readOnly}
                onClick={
                    byGoogle
                        ? () => void connect()
                        : () => void run("primary", () => claimMyAccess(key, true, key === "website" ? builder : undefined))
                }
            >
                {byGoogle ? "Connect Google" : "Mark as done"}
            </PillButton>
        );
    } else if (item.status === "claimed") {
        action = (
            <PillButton className={TAP} busy={busy === "undo"} writes disabled={readOnly} onClick={() => void run("undo", () => claimMyAccess(key, false))}>
                Undo
            </PillButton>
        );
    } else if (held) {
        action = (
            <PillButton className={TAP} data-access-row={key} onClick={onManage}>
                Manage
            </PillButton>
        );
    }

    return (
        // A connected row opens its access drawer from anywhere on it; Manage
        // stays the keyboard way in. Rows still to do have their own buttons.
        <div
            onClick={held ? rowClick(onManage) : undefined}
            className={cn(
                "py-4",
                held && "cursor-pointer transition-colors hover:bg-emerald-100/70",
                embedded ? "px-3" : "px-5",
                // Embedded, a done row is its own band: no hairline through its green.
                embedded && held ? "my-1 rounded-xl bg-emerald-50" : "border-t first:border-t-0",
                !embedded && held ? "border-emerald-100 bg-emerald-50" : "border-gray-100",
            )}
        >
            <div className="flex items-start gap-3">
                <IconSquare icon={ROW_ICON[key].icon} tone={held ? "green" : "gray"} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 text-gray-900")}>{MERCHANT_ACCESS_NAME[key]}</p>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{item.status === "claimed" ? WAITING_NOTE[key] : ACCESS_WHY[key]}</p>
                    {item.status === "not_started" && (
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-controls={stepsId}
                            onClick={() => setOpen((v) => !v)}
                            className={cn(TYPE.tc, "mt-2 inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                        >
                            How to connect?
                            <ChevronDownIcon aria-hidden size={14} className={cn("transition-transform duration-200 motion-reduce:transition-none", open && "rotate-180")} />
                        </button>
                    )}
                </div>
                {/* The status sits by the button, not on a line of its own.
                    "Not started" is left out: the button already says it. On a
                    phone the title needs the room; the green row and the
                    reason line say the same. */}
                <div className={cn(TYPE.tc, "flex shrink-0 items-center gap-3 text-gray-500")}>
                    {item.status !== "not_started" && (
                        <span className="hidden sm:inline-flex">
                            <AccessStatusLine item={item} />
                        </span>
                    )}
                    {action}
                </div>
            </div>

            {item.status === "not_started" && open && (
                <div id={stepsId} className="mt-3 rounded-xl bg-gray-50 p-4 sm:ml-11">
                    {key === "gbp" && (
                        <>
                            <GuideSteps steps={GBP_STEPS} />
                            <AccessAddress className="mt-3 bg-white ring-1 ring-inset ring-black/[0.06]" />
                            {onboarding.outlets > 1 && (
                                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>Do this once per outlet. You have {onboarding.outlets}.</p>
                            )}
                        </>
                    )}
                    {key === "website" && (
                        <>
                            <p className={cn(TYPE.tc, "m-0 mb-2 text-gray-500")}>What your site runs on</p>
                            <Seg<SiteBuilder>
                                ariaLabel="What your site runs on"
                                className="max-sm:[&_button]:h-9"
                                value={builder}
                                onChange={(next) => {
                                    setBuilder(next);
                                    setFailure(null);
                                }}
                                items={SITE_BUILDER_ORDER.map((k) => ({ key: k, label: SITE_GUIDES[k].short }))}
                            />
                            <GuideSteps steps={guide.steps} className="mt-3" />
                            <AccessAddress className="mt-3 bg-white ring-1 ring-inset ring-black/[0.06]" />
                        </>
                    )}
                    {byGoogle && <GuideSteps steps={GOOGLE_STEPS} />}
                    {byGoogle && onboarding.claimableAccess?.includes(key) === true && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <p className={cn(TYPE.tb, "m-0 mb-1 font-semibold text-gray-900")}>Or add us as a user</p>
                            <GuideSteps steps={key === "gsc" ? ADD_USER_STEPS.gsc : ADD_USER_STEPS.ga4} />
                            <AccessAddress className="mt-3 bg-white ring-1 ring-inset ring-black/[0.06]" />
                            <PillButton
                                variant="primary"
                                className={cn(TAP, "mt-3")}
                                busy={busy === "primary"}
                                writes
                                disabled={readOnly || busy !== null}
                                onClick={() => void run("primary", () => claimMyAccess(key, true))}
                            >
                                I've added you
                            </PillButton>
                        </div>
                    )}
                </div>
            )}

            {failure && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-3 rounded-xl bg-rose-50 px-3 py-2 font-medium text-rose-700 sm:ml-11")}>
                    {failure}
                </p>
            )}
        </div>
    );
}

/**
 * "Someone else looks after this?" as a button first (redesign). The form is
 * for the few owners who hand this on, so it opens on request, and focus goes
 * to its first field. Once the steps have gone to someone, the card stays
 * open: the "Sent to" line is the record of it.
 */
function ShareStepsAsk({
    onboarding,
    onChanged,
    embedded = false,
}: {
    onboarding: MerchantOnboarding;
    onChanged: (updated: MerchantOnboarding) => void;
    embedded?: boolean;
}) {
    const [open, setOpen] = useState(onboarding.sharedWith.length > 0);
    const asked = useRef(false);

    useEffect(() => {
        if (!open || !asked.current) return;
        const field = document.getElementById(NAME_ID);
        field?.scrollIntoView({ block: "center", behavior: "smooth" });
        field?.focus({ preventScroll: true });
    }, [open]);

    if (open) return <ShareSteps onboarding={onboarding} onChanged={onChanged} embedded={embedded} />;
    return (
        <PillButton
            icon={SendIcon}
            writes
            className={cn(TAP, embedded && "ml-3")}
            onClick={() => {
                asked.current = true;
                setOpen(true);
            }}
        >
            Someone else looks after this?
        </PillButton>
    );
}
